import React from 'react';
import { useHistory } from 'react-router';
import { getRoutePlansAndBillings } from 'modules/v2/routes/navigation';
import { cn } from 'modules/v2/common/utils/cn';
import {
  formatNoticeDate,
  planLabel,
  SubscriptionNotice,
} from 'modules/v2/utils/subscriptionNotice';

interface SubscriptionNoticeBannerProps {
  notice: SubscriptionNotice;
  onDismiss: () => void;
}

const WarningIcon = () => (
  <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
    />
  </svg>
);

const InfoIcon = () => (
  <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const CheckIcon = () => (
  <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const STYLES = {
  cancellation: 'bg-error-500 text-white',
  scheduledPlanChange: 'bg-secondary-blue-600 text-white',
  planChanged: 'bg-success-500 text-white',
};

const ACTION_BUTTON =
  'shrink-0 px-3 py-1 text-xs font-semibold rounded bg-white hover:bg-neutral-100 transition-colors';

/**
 * The subscription notice shown under the nav bar: a scheduled cancellation
 * (red, links to Plans & billing to undo it), a scheduled plan change (blue)
 * or a completed plan change (green, dismissible).
 */
export const SubscriptionNoticeBanner: React.FC<SubscriptionNoticeBannerProps> = ({
  notice,
  onDismiss,
}) => {
  const history = useHistory();
  const goToBilling = () => history.push(getRoutePlansAndBillings());

  let icon: React.ReactNode;
  let message: React.ReactNode;
  let action: React.ReactNode;

  if (notice.kind === 'cancellation') {
    icon = <WarningIcon />;
    message = (
      <>
        Your membership will be canceled on{' '}
        <span className="font-bold underline">{formatNoticeDate(notice.date)}</span>.
        <span className="hidden lg:inline"> You will lose access to your books, leads and credits.</span>
      </>
    );
    action = (
      <button type="button" onClick={goToBilling} className={cn(ACTION_BUTTON, 'text-error-600')}>
        Keep my membership
      </button>
    );
  } else if (notice.kind === 'scheduledPlanChange') {
    icon = <InfoIcon />;
    message = (
      <>
        Your plan will change to <span className="font-bold">{planLabel(notice.change)}</span> on{' '}
        <span className="font-bold">{formatNoticeDate(notice.change.effectiveAt)}</span>.
      </>
    );
    action = (
      <button
        type="button"
        onClick={goToBilling}
        className={cn(ACTION_BUTTON, 'text-secondary-blue-700')}
      >
        View plan
      </button>
    );
  } else {
    icon = <CheckIcon />;
    message = (
      <>
        Your plan changed to <span className="font-bold">{planLabel(notice.change)}</span> on{' '}
        {formatNoticeDate(notice.change.effectiveAt)}.
      </>
    );
    action = (
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="shrink-0 p-1 rounded hover:bg-white/20 transition-colors"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    );
  }

  return (
    <div
      role={notice.kind === 'cancellation' ? 'alert' : 'status'}
      className={cn(
        'fixed top-[62px] left-0 right-0 h-[46px] z-[998] flex items-center justify-center px-4 text-xs sm:text-sm shadow-md',
        STYLES[notice.kind],
      )}
    >
      <div className="flex items-center gap-3 min-w-0">
        {icon}
        <span className="font-medium truncate sm:whitespace-normal">{message}</span>
        {action}
      </div>
    </div>
  );
};
